import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Send, Minimize2 } from "lucide-react";

const BOT_NAME = "Spark";
const COMPANY_EMAIL = "info@quantumsparktechnology.com";

const QUICK_REPLIES = [
  { label: "Services we offer", key: "services" },
  { label: "How to get a quote", key: "quote" },
  { label: "Project timelines", key: "timeline" },
  { label: "Talk to a human", key: "human" },
];

const BOT_RESPONSES = {
  services: {
    text: "We cover 6 core areas:\n\n• **UI/UX Design** — wireframes, prototypes, design systems\n• **Website Development** — React, Next.js, SaaS platforms\n• **Mobile Apps** — iOS, Android, React Native, Flutter\n• **AI Development** — LLM integrations, automation\n• **Blockchain** — smart contracts, DApps, Web3\n• **Quality Assurance** — automated testing, audits\n\nWant details on any specific service?",
    follow: ["Website Development", "Mobile Apps", "AI Development", "Get a quote"],
  },
  quote: {
    text: "Getting a quote is easy and free:\n\n1. Use our **Project Estimator** on the homepage for an instant ballpark\n2. Or click below to fill in the contact form — we respond within 24 hours\n\nNo commitment required.",
    follow: ["Use estimator", "Contact form"],
    actions: [
      { label: "Get estimate →", href: "/#estimator" },
      { label: "Contact us →", href: "/#contact" },
    ],
  },
  timeline: {
    text: "Typical timelines by project type:\n\n• **Website / Marketing Site** — 4–6 weeks\n• **Web App / SaaS** — 8–12 weeks\n• **Mobile App** — 10–14 weeks\n• **AI / ML Product** — 10–16 weeks\n\nAll projects start with a discovery phase to pin down exact scope before committing to dates.",
    follow: ["How to get a quote", "Talk to a human"],
  },
  human: {
    text: `Our team is available via email and typically replies within 24 hours. You can also use the contact form to describe your project in detail.\n\n📧 ${COMPANY_EMAIL}`,
    follow: ["Contact form"],
    actions: [
      { label: "Open contact form →", href: "/#contact" },
      { label: `Email us →`, href: `mailto:${COMPANY_EMAIL}` },
    ],
  },
  "website development": {
    text: "We build React and Next.js applications with clean architecture, SEO-friendly structure, CMS options, and performance budgets built in.\n\nDeliverables typically include: component library, responsive design, deployment, and handoff docs.",
    follow: ["How to get a quote", "Talk to a human"],
  },
  "mobile apps": {
    text: "We build for iOS and Android using React Native or Flutter when speed matters, and native stacks when platform depth is required. All builds include store submission support and backend integration.",
    follow: ["How to get a quote", "Talk to a human"],
  },
  "ai development": {
    text: "We integrate LLMs and automation into production products carefully — clear prompts, retrieval-augmented generation where needed, monitoring, and UX that keeps humans in control of critical decisions.",
    follow: ["How to get a quote", "Talk to a human"],
  },
  "contact form": {
    text: "Scroll down to the contact section, fill in your name, email, and a brief project description — we'll get back to you within 24 hours with a suggested next step.",
    follow: ["Talk to a human"],
    actions: [{ label: "Take me there →", href: "/#contact" }],
  },
  "use estimator": {
    text: "The Project Estimator is on the homepage. Pick your project type, features, team size, and urgency — it gives you a live price and timeline range instantly.",
    follow: ["How to get a quote"],
    actions: [{ label: "Open estimator →", href: "/#estimator" }],
  },
  "get a quote": {
    text: "The fastest way is our free discovery call. Fill in the contact form with your project goals and we'll reach out within 24 hours to set it up.",
    follow: ["Talk to a human"],
    actions: [{ label: "Contact form →", href: "/#contact" }],
  },
  default: {
    text: "I'm not sure about that one — but our team definitely can help! Use the contact form or email us directly and we'll get back to you within 24 hours.",
    follow: ["Talk to a human", "Services we offer"],
    actions: [{ label: "Contact us →", href: "/#contact" }],
  },
};

function getBotResponse(input) {
  const key = input.toLowerCase().trim();
  return (
    BOT_RESPONSES[key] ||
    (key.includes("service") ? BOT_RESPONSES.services : null) ||
    (key.includes("quote") || key.includes("price") || key.includes("cost") ? BOT_RESPONSES.quote : null) ||
    (key.includes("time") || key.includes("week") || key.includes("long") ? BOT_RESPONSES.timeline : null) ||
    (key.includes("human") || key.includes("person") || key.includes("agent") || key.includes("team") ? BOT_RESPONSES.human : null) ||
    (key.includes("mobile") || key.includes("app") || key.includes("ios") || key.includes("android") ? BOT_RESPONSES["mobile apps"] : null) ||
    (key.includes("ai") || key.includes("ml") || key.includes("intelligence") ? BOT_RESPONSES["ai development"] : null) ||
    (key.includes("web") && !key.includes("3") ? BOT_RESPONSES["website development"] : null) ||
    BOT_RESPONSES.default
  );
}

function formatText(text) {
  return text.split("\n").map((line, i) => {
    const bold = line.replace(/\*\*(.*?)\*\*/g, (_, m) => `<strong>${m}</strong>`);
    return (
      <span key={i} dangerouslySetInnerHTML={{ __html: bold }} className="block leading-relaxed" />
    );
  });
}

const GREETING = {
  id: 0,
  from: "bot",
  text: "Hi there 👋 I'm **Spark**, your assistant from Quantum Spark Technology.\n\nI can tell you about our services, timelines, and pricing — or connect you with our team for a real conversation.",
  quick: QUICK_REPLIES,
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setMinimised(false);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  useEffect(() => {
    if (open && !minimised) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, minimised]);

  const sendUserMessage = (text) => {
    if (!text.trim()) return;
    const userMsg = { id: Date.now(), from: "user", text };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setTyping(true);

    const delay = 600 + Math.random() * 600;
    setTimeout(() => {
      const res = getBotResponse(text);
      setMessages((m) => [
        ...m,
        {
          id: Date.now() + 1,
          from: "bot",
          text: res.text,
          quick: res.follow
            ? res.follow.map((label) => ({ label, key: label.toLowerCase() }))
            : null,
          actions: res.actions || null,
        },
      ]);
      setTyping(false);
      if (!open) setUnread((n) => n + 1);
    }, delay);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendUserMessage(input);
  };

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-3">
        <AnimatePresence>
          {!open && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-w-[200px] rounded-2xl rounded-br-sm bg-white px-4 py-2.5 text-sm font-medium text-zinc-700 shadow-lg border border-zinc-200"
            >
              Chat with us — we're online!
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close chat" : "Open chat"}
          className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#6F00FF] text-white shadow-[0_8px_30px_rgb(111,0,255,0.35)] transition-transform hover:scale-105 active:scale-95"
        >
          <AnimatePresence mode="wait">
            {open ? (
              <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                <X size={22} />
              </motion.span>
            ) : (
              <motion.span key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                <MessageCircle size={22} />
              </motion.span>
            )}
          </AnimatePresence>

          {unread > 0 && !open && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </button>
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="fixed bottom-24 right-6 z-[9998] flex w-[360px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-[1.5rem] border border-zinc-200 bg-white shadow-2xl shadow-zinc-900/15"
            style={{ maxHeight: minimised ? "auto" : "540px" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-[#6F00FF] px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white">
                  <MessageCircle size={18} />
                  <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-[#6F00FF] bg-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{BOT_NAME} — QST Assistant</p>
                  <p className="text-xs text-white/70">We're online • typically &lt;24h reply</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setMinimised((v) => !v)}
                  aria-label="Minimise"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <Minimize2 size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {!minimised && (
              <>
                {/* Messages */}
                <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-5" style={{ maxHeight: "380px" }}>
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex flex-col ${msg.from === "user" ? "items-end" : "items-start"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                          msg.from === "user"
                            ? "rounded-br-sm bg-[#6F00FF] text-white"
                            : "rounded-bl-sm bg-zinc-100 text-zinc-800"
                        }`}
                      >
                        {formatText(msg.text)}
                      </div>

                      {msg.actions && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {msg.actions.map((action, i) => (
                            <a
                              key={i}
                              href={action.href}
                              onClick={() => setOpen(false)}
                              className="inline-flex items-center gap-1 rounded-full border border-[#6F00FF]/20 bg-[#6F00FF]/5 px-3 py-1.5 text-xs font-semibold text-[#6F00FF] transition-colors hover:bg-[#6F00FF]/10"
                            >
                              {action.label}
                            </a>
                          ))}
                        </div>
                      )}

                      {msg.quick && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {msg.quick.map((q, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => sendUserMessage(q.label)}
                              className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:border-[#6F00FF]/40 hover:bg-[#6F00FF]/5 hover:text-[#6F00FF]"
                            >
                              {q.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {typing && (
                    <div className="flex items-start">
                      <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-zinc-100 px-4 py-3">
                        {[0, 1, 2].map((i) => (
                          <span
                            key={i}
                            className="h-2 w-2 rounded-full bg-zinc-400 animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div ref={bottomRef} />
                </div>

                {/* Live chat notice */}
                <div className="border-t border-zinc-100 bg-zinc-50 px-4 py-2.5 text-center">
                  <p className="text-xs text-zinc-500">
                    Want a real conversation?{" "}
                    <a
                      href={`mailto:${COMPANY_EMAIL}`}
                      className="font-semibold text-[#6F00FF] hover:underline"
                    >
                      Email our team
                    </a>
                    {" "}or{" "}
                    <a
                      href="/#contact"
                      onClick={() => setOpen(false)}
                      className="font-semibold text-[#6F00FF] hover:underline"
                    >
                      use the contact form
                    </a>
                    .
                  </p>
                </div>

                {/* Input */}
                <form onSubmit={handleSubmit} className="flex items-center gap-2 border-t border-zinc-100 px-4 py-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask anything…"
                    className="flex-1 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-2 text-sm outline-none focus:border-[#6F00FF]/40 focus:ring-2 focus:ring-[#6F00FF]/10"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim()}
                    aria-label="Send"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#6F00FF] text-white transition-opacity disabled:opacity-40"
                  >
                    <Send size={15} />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
